import { Box, Divider } from '@chakra-ui/react';
import { Colors, Icon, Text } from '@mercator-gcp/grout';
import { Link } from 'components/elements/Link';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import SaveProject from 'components/widgets/SaveProject';
import { useProjectSharedListsOnPdp } from 'hooks/useProjectSharedListsOnPdp';
import type { CollectionSharedList } from 'types';

/** Companies Involved: chevron sits against the name via flex-start + small gap (not space-between) */
const titleRowStyle = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 'padding_2',
};

/** Title may truncate; reserve room for chevron (same size as InvolvedCompanies) */
const titleTruncateBox = {
  flex: '0 1 auto',
  minWidth: 0,
  maxWidth: 'calc(100% - 18px)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const entryOuterStyle = {
  width: '100%',
  minWidth: 0,
  borderLeft: `3px solid ${Colors.ForestGreen900}`,
  paddingLeft: 'padding_8',
  paddingRight: 'padding_8',
  paddingY: 'padding_8',
};

const nameByStackStyle = {
  gap: '2px',
  width: '100%',
  minWidth: 0,
  alignItems: 'flex-start',
};

const bodyStackStyle = {
  gap: 'padding_8',
  width: '100%',
  minWidth: 0,
  alignItems: 'flex-start',
};

const sharedListsSection = {
  marginTop: 'padding_16',
  gap: '8px',
};

const listDividerStyle = {
  borderColor: Colors.ArmyGreen200,
};

function formatSharedByLine(sharedBy: CollectionSharedList['shared_by']) {
  const fullName = [sharedBy.first_name, sharedBy.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (fullName) {
    return `By ${fullName}`;
  }

  return `By ${sharedBy.email}`;
}

type TProps = {
  projectSlug: string;
  documentId?: string;
};

const SharedProjectListsOnPdp = ({ projectSlug, documentId }: TProps) => {
  const { data: entries, isLoading, isError } = useProjectSharedListsOnPdp(projectSlug);

  if (isLoading || isError || !entries?.length) {
    return null;
  }

  const multipleLists = entries.length > 1;

  return (
    <FlexColumn sx={sharedListsSection}>
      <Text variant="smallLabel" color={Colors.TextQuiet}>
        On Shared Lead Lists
      </Text>
      {entries.map((entry, index) => {
        const listLabel = entry.name?.trim() || 'Untitled lead list';
        const sharedByLine = formatSharedByLine(entry.shared_by);
        const dashboardHref = `/dashboard/shared?shared=${entry.collection_id}`;

        return (
          <FlexColumn key={entry.collection_id} sx={{ width: '100%', minWidth: 0 }}>
            {multipleLists && index > 0 ? <Divider sx={listDividerStyle} /> : null}
            <Box sx={entryOuterStyle}>
              <FlexColumn sx={bodyStackStyle}>
                <FlexColumn sx={nameByStackStyle}>
                  <Link
                    to={dashboardHref}
                    sx={{
                      display: 'block',
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      color: 'brand.army-green',
                      textDecoration: 'none',
                      _hover: { textDecoration: 'underline' },
                    }}
                  >
                    <FlexRow sx={titleRowStyle}>
                      <Box sx={titleTruncateBox}>
                        <Text.BodyBold sx={{ lineHeight: 1.25 }}>
                          {listLabel}
                        </Text.BodyBold>
                      </Box>
                      <Box flexShrink={0} lineHeight={0}>
                        <Icon
                          name={Icon.Name.ChevronRight}
                          fill={Colors.ForestGreen900}
                          size={14}
                        />
                      </Box>
                    </FlexRow>
                  </Link>
                  <Text variant="small" color={Colors.TextQuiet}>
                    {sharedByLine}
                  </Text>
                </FlexColumn>
                <SaveProject
                  projectSlug={projectSlug}
                  documentId={documentId}
                  savedProject={entry.saved_project}
                  isSharedCollection
                  displayBorders={{ savedMenu: false, unsavedMenu: false }}
                />
              </FlexColumn>
            </Box>
          </FlexColumn>
        );
      })}
    </FlexColumn>
  );
};

export default SharedProjectListsOnPdp;
